<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import Box from '@uiKit/elements/Box.vue'
import Button from '@uiKit/elements/Button.vue'
import Card from '@uiKit/components/Card.vue'
import ApproveDialog from '@uiKit/components/ApproveDialog.vue'
import Message from '@uiKit/elements/Message.vue'
import type { TableAction } from '@uiKit/components/DataTableV2.vue'

import Text from '@uiKit/elements/Text.vue'

import { debounce } from '@shared/utils'
import { toErrorMessage } from '@core/api/normalizeError'
import { useWalletManagementListQuery } from '../api/queries/useWalletManagementListQuery'
import { useCreateCustodyWalletMutation } from '../api/mutations/useCreateCustodyWalletMutation'
import { useApproveCustodyWalletMutation } from '../api/mutations/useApproveCustodyWalletMutation'
import { useAppToast } from '@shared/composables/useAppToast'
import { CRYPTO_WALLET_STATUS } from '../constants/cryptoWalletStatus'
import { TRUSTLINE_STATUS, type TrustlineStatus } from '../constants/trustlineStatus'
import {
  WALLET_MANAGEMENT_QUEUE_HEADING,
  WALLET_MANAGEMENT_ROUTE,
} from '../constants/walletManagementQueue'
import WalletManagementList from '../components/WalletManagementList.vue'
import type { WalletManagementRow } from '../types/cryptoWalletTypes'

const SEARCH_DEBOUNCE_MS = 400
const DEFAULT_PAGE_SIZE = 20

const route = useRoute()
const router = useRouter()

// The screen serves three work queues off one component, told apart by ROUTE
// NAME. `Trustline approvals` is the awaiting-opt-in queue; `Failed on-chain ops`
// is the failed-trustline queue; the bare route is everything.
const routeName = computed(() => String(route.name ?? ''))

const awaitingOptInOnly = computed(
  () => routeName.value === WALLET_MANAGEMENT_ROUTE.TRUSTLINE_APPROVALS,
)

const trustlineStatusFilter = computed<TrustlineStatus | undefined>(() =>
  routeName.value === WALLET_MANAGEMENT_ROUTE.FAILED_ON_CHAIN ? TRUSTLINE_STATUS.FAILED : undefined,
)

const queueTitle = computed(() => WALLET_MANAGEMENT_QUEUE_HEADING[routeName.value] ?? '')

const searchQuery = ref('')
const debouncedSearch = ref('')
const currentPage = ref(1)
const rowsPerPage = ref(DEFAULT_PAGE_SIZE)

const updateDebouncedSearch = debounce((value: string) => {
  debouncedSearch.value = value
  // A new search starts from the first page — keeping the old page number would
  // show an empty table for any result set shorter than the current offset.
  currentPage.value = 1
}, SEARCH_DEBOUNCE_MS)

watch(searchQuery, (value) => updateDebouncedSearch(value))

const filters = computed(() => ({
  page: currentPage.value,
  limit: rowsPerPage.value,
  search: debouncedSearch.value || undefined,
  ...(awaitingOptInOnly.value && { awaitingOptInOnly: true }),
  ...(trustlineStatusFilter.value && { trustlineStatus: trustlineStatusFilter.value }),
}))

// Entering a different queue is a different result set, so the page resets with
// it — otherwise a deep page number carries over and shows an empty table.
// Moving between queues is a different result set, so the page resets with it —
// otherwise a deep page number carries over and shows an empty table.
watch(routeName, () => {
  currentPage.value = 1
})

// `error` is bound, not just `data`: a failed request otherwise renders as the
// table's own "No investors found" empty state, which reads as "there are no
// investors" — the reason a 500 from this endpoint went unnoticed as an empty
// screen rather than surfacing as a failure.
const {
  data,
  isLoading,
  error: listError,
  refetch: refetchList,
} = useWalletManagementListQuery(filters)

const { showSuccess, showError } = useAppToast()
const { mutate: createCustodyWallet } = useCreateCustodyWalletMutation()
const { mutate: approveCustodyWallet, isPending: isApproving } =
  useApproveCustodyWalletMutation()

// One action at a time, keyed by investor, so only the row being acted on is
// disabled. Shared by create and approve — a row is never doing both.
const busyInvestorId = ref('')

function handleCreateWallet(row: WalletManagementRow) {
  busyInvestorId.value = row.investorId
  createCustodyWallet(row.investorId, {
    onSuccess: (wallet) => {
      showSuccess('Custody wallet created', `Funded on XRPL at ${wallet.walletAddress}.`)
    },
    onError: (error: unknown) => {
      showError(
        'Could not create the wallet',
        toErrorMessage(error, 'An error occurred while creating the custody wallet.'),
      )
    },
    onSettled: () => {
      busyInvestorId.value = ''
    },
  })
}

// ── Approve ── the row the operator chose, held while the ApproveDialog is open.
const walletToApprove = ref<WalletManagementRow | null>(null)
const approveDialogVisible = ref(false)

// Approval is what lets held orders deliver to this wallet, so the dialog
// names the investor rather than asking a bare "Are you sure?".
const approveDialogMessage = computed(() => {
  const row = walletToApprove.value
  if (!row) return ''
  return `Approve the custody wallet for ${row.investorName || row.email}. Its status changes to Active and orders held for this investor can be delivered to it.`
})

function handleApproveWallet(row: WalletManagementRow) {
  walletToApprove.value = row
  approveDialogVisible.value = true
}

function submitApproval() {
  const row = walletToApprove.value
  if (!row) return
  busyInvestorId.value = row.investorId
  approveCustodyWallet(
    { investorId: row.investorId, walletId: row.walletId },
    {
      onSuccess: () => {
        approveDialogVisible.value = false
        walletToApprove.value = null
        showSuccess('Wallet approved', 'The custody wallet is now active.')
      },
      onError: (error: unknown) => {
        showError(
          'Could not approve the wallet',
          toErrorMessage(error, 'An error occurred while approving the custody wallet.'),
        )
      },
      onSettled: () => {
        busyInvestorId.value = ''
      },
    },
  )
}

const isPendingValidation = (row: WalletManagementRow) =>
  Boolean(row.walletAddress) && row.walletStatus === CRYPTO_WALLET_STATUS.PENDING_VALIDATION

function openInvestor(row: WalletManagementRow) {
  void router.push({
    name: WALLET_MANAGEMENT_ROUTE.DETAIL,
    params: { investorId: row.investorId },
  })
}

// Both actions open the same page; the labels differ so the queue says what is
// waiting there rather than making the operator discover it. Setup cannot happen
// inline from this table: a trustline is opened per OFFERING, and one investor
// can have several orders held across different properties — the row has no way
// to say which one a single click meant.
const rowActions: TableAction<WalletManagementRow>[] = [
  // Offered straight from the table because the CUSTODY WALLET column already
  // reports "Not created" here — a screen that names a gap should let the
  // operator close it without a detour through the detail page.
  {
    label: 'Create custody wallet',
    icon: 'wallet',
    hidden: (row) => Boolean(row.walletAddress),
    disabled: (row) => busyInvestorId.value === row.investorId,
    onClick: (row) => handleCreateWallet(row),
  },
  {
    label: 'Approve',
    icon: 'check',
    hidden: (row) => !isPendingValidation(row),
    disabled: (row) => busyInvestorId.value === row.investorId,
    onClick: (row) => handleApproveWallet(row),
  },
  {
    label: 'Set up trustline',
    icon: 'link',
    hidden: (row) => row.awaitingOptInOrders === 0,
    onClick: (row) => openInvestor(row),
  },
  { label: 'Manage wallet', icon: 'eye', onClick: (row) => openInvestor(row) },
]
</script>

<template>
  <Box flex direction="col" gap="4">
    <Text v-if="queueTitle" type="body" weight="medium">{{ queueTitle }}</Text>

    <Message v-if="listError" color="danger" variant="outlined" size="small">
      <Box flex justify="between" align="center" gap="3">
        <Text type="body-sm">
          Could not load investor wallets. {{ toErrorMessage(listError, '') }}
        </Text>
        <Button label="Try again" variant="text" color="danger" @click="refetchList()" />
      </Box>
    </Message>

    <Card variant="outlined" padding="0" class="table-card">
      <WalletManagementList
        :rows="data?.rows ?? []"
        :loading="isLoading"
        :total="data?.rowsCount ?? 0"
        :page="currentPage"
        :page-size="rowsPerPage"
        :search="searchQuery"
        :actions="rowActions"
        @update:search="searchQuery = $event"
        @update:page="currentPage = $event"
        @select="openInvestor"
      />
    </Card>

  </Box>

  <ApproveDialog
    v-model:visible="approveDialogVisible"
    title="Approve custody wallet"
    entity-type="Custody wallet"
    :message="approveDialogMessage"
    confirm-label="Approve wallet"
    :loading="isApproving"
    @confirm="submitApproval"
  />
</template>
